import { API_BASE_URL } from "src/config/apiBase";
import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { User, Calendar, Tag, Eye } from "lucide-react";
import ReactionBar from "./ReactionBar";
import CommentSection from "./CommentSection";
import ShareButtons from "./ShareButton";
import BlogImageSlider from "./BlogImageSlider";
import { decryptToken } from "../utils/getToken";
import { getSessionMainId, getDecryptedUserFromCookie } from "../utils/getCookie";
import DOMPurify from "dompurify";
import { descriptionContentToHtml } from "./blogContentFormat";
import "./BlogContent.css";

const parseContent = (content) => {
    if (!content) return content;
    if (typeof content === "object") return content;
    try {
        let parsed = JSON.parse(content);
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
        return parsed;
    } catch {
        return content;
    }
};

const applySections = (data) => ({
    ...data,
    sections: (data.sections || []).map((sec) => ({
        ...sec,
        content: parseContent(sec.content),
    })),
});

const AdminBlogDetail = () => {
    const { slug, id } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const url = API_BASE_URL;
    const [view, setView] = useState(0);
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const [enteredPassword, setEnteredPassword] = useState("");
    const token = decryptToken("accessToken") || "";
    const mainId = getSessionMainId();
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const [storeWebsite, setStoreWebsite] = useState("");

    const authHeaders = {
        Authorization: `Bearer ${token}`,
    };

    const fetchViewCount = async (blogId) => {
        if (!blogId) return;
        try {
            const response = await fetch(`${url}/blog-view/${blogId}`, {
                headers: authHeaders,
            });
            const res = await response.json();
            setView(res.views ?? 0);
        } catch (err) {
            console.log(err);
        }
    };

    const loadBlog = async () => {
        setLoading(true);
        setError(false);

        try {
            let res = await fetch(
                `${url}/blog/slug/${mainId}/${encodeURIComponent(slug)}`,
                { headers: authHeaders }
            );

            if (!res.ok && id) {
                res = await fetch(`${url}/blog/single/${id}`, {
                    headers: authHeaders,
                });
            }

            const datanew = await res.json();
            if (!datanew?.data) throw new Error("No data found");

            const data = datanew.data;

            if (data.visibility === "Password" && !data.sections?.length) {
                setShowPasswordPopup(true);
                setBlog(data);
                return;
            }

            setBlog(applySections(data));
            if (data.id) fetchViewCount(data.id);
        } catch (err) {
            console.error("Error loading blog:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleUnlock = async () => {
        if (!blog?.slug) return;
        try {
            const res = await fetch(`${url}/blog/unlock/${blog.slug}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...authHeaders,
                },
                body: JSON.stringify({
                    password: enteredPassword,
                    main_id: mainId,
                }),
            });
            const data = await res.json();
            if (res.ok && data.data) {
                setBlog(applySections(data.data));
                setShowPasswordPopup(false);
                setEnteredPassword("");
                if (data.data.id) fetchViewCount(data.data.id);
            } else {
                alert(data.error || "Incorrect password");
            }
        } catch {
            alert("Error verifying password");
        }
    };

    useEffect(() => {
        if (!slug && !id) return;
        loadBlog();
    }, [slug, id]);

    useEffect(() => {
        const fetchStoreWebsite = async () => {
            if (!isLoggedIn?.main_id) return;
            try {
                const res = await fetch(`${url}/register/single/${isLoggedIn.main_id}`, {
                    headers: authHeaders,
                });
                const data = await res.json();
                const link = (data?.users?.website_link || "").replace(/\/$/, "");
                setStoreWebsite(link);
            } catch {
                /* optional */
            }
        };
        fetchStoreWebsite();
    }, [isLoggedIn?.main_id]);

    const renderDescription = (content) => {
        const html = descriptionContentToHtml(content);
        if (!html) return null;

        return (
            <div
                className="blog-content"
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(html, {
                        ADD_TAGS: ["iframe"],
                        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "src", "style"],
                    }),
                }}
            />
        );
    };

    if (loading) {
        return <p className="text-center text-[#4e59c2]">Loading blog...</p>;
    }

    if (error || !blog) {
        return <p className="text-center text-red-500">Blog not found.</p>;
    }

    if (showPasswordPopup) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900">Password protected preview</h2>
                    <p className="text-sm text-gray-600">Enter the post password to preview full content.</p>
                    <input
                        type="password"
                        value={enteredPassword}
                        onChange={(e) => setEnteredPassword(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#4e59c2] focus:outline-none"
                        placeholder="Password"
                        aria-label="Post password"
                    />
                    <div className="flex gap-2 justify-end">
                        <button
                            type="button"
                            onClick={() => {
                                setEnteredPassword("");
                                setShowPasswordPopup(false);
                            }}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleUnlock}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#4e59c2] rounded-lg hover:bg-[#3d47a8]"
                        >
                            Unlock
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const formattedDate = new Date(
        blog.published_at || blog.publishedAt || blog.createdAt
    ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    const sortedSections = [...(blog.sections || [])].sort(
        (a, b) => a.order - b.order
    );

    const categories =
        blog.categories?.map((c) => c.categoryDetails?.name).join(", ") ||
        "Uncategorized";

    const publicBlogUrl =
        storeWebsite && blog.slug
            ? `${storeWebsite}/blog/${blog.slug}`
            : window.location.href;

    return (
        <div className="max-w-3xl mx-auto px-4 py-8 text-black">
            {(blog.featured || blog.isFeatured) && (
                <span className="mb-4 inline-block rounded-full bg-[#e2e7f7] px-3 py-1 text-xs font-medium text-[#4449b1]">
                    Featured
                </span>
            )}

            <h1 className="text-2xl font-bold text-black mb-4">{blog.title}</h1>

            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-5">
                <span className="flex items-center gap-1">
                    <User size={14} /> {blog.author_name || "Unknown Author"}
                </span>
                <span className="flex items-center gap-1">
                    <Calendar size={14} /> {formattedDate}
                </span>
                <span className="flex items-center gap-1">
                    <Tag size={14} /> {categories}
                </span>
                <span className="flex items-center gap-1">
                    <Eye size={14} /> {view} views
                </span>
                {blog.reading_time_minutes > 0 && (
                    <span>{blog.reading_time_minutes} min read</span>
                )}
            </div>

            {blog.feature_image && (
                <img
                    src={`${url}${blog.feature_image}`}
                    alt={blog.title}
                    className="w-full rounded-lg shadow mb-6"
                />
            )}

            <div className="prose max-w-none mb-6">
                {sortedSections.map((section) => {
                    switch (section.type) {
                        case "description":
                            return (
                                <div key={section.id} className="mb-6">
                                    {renderDescription(section.content)}
                                </div>
                            );

                        case "image": {
                            const images = Array.isArray(section.content?.images)
                                ? section.content.images.map((img) => ({
                                      url: img.url,
                                      title: img.title || "",
                                  }))
                                : [];

                            return (
                                <div key={section.id} className="my-6">
                                    {images.length === 1 && (
                                        <>
                                            <img
                                                src={`${url}${images[0].url}`}
                                                alt={images[0].title || "Blog Image"}
                                                className="w-full rounded-lg shadow"
                                            />
                                            {images[0].title && (
                                                <p className="text-sm text-center text-gray-600 mt-2">
                                                    {images[0].title}
                                                </p>
                                            )}
                                        </>
                                    )}
                                    {images.length > 1 && (
                                        <BlogImageSlider images={images} />
                                    )}
                                </div>
                            );
                        }

                        case "button":
                            return (
                                <div key={section.id} className="text-center my-8">
                                    <a
                                        href={section.content?.action}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-[#4e59c2] text-white text-sm font-medium px-5 py-2.5 rounded-lg shadow hover:bg-[#4449b1] transition"
                                    >
                                        {section.content?.text || "Click Here"}
                                    </a>
                                </div>
                            );

                        default:
                            return null;
                    }
                })}
            </div>

            <div className="mt-8">
                <h4 className="text-sm font-semibold mb-2 text-gray-700">Tags:</h4>
                <div className="flex flex-wrap gap-2">
                    {blog.tags?.map((tag) => (
                        <span
                            key={tag.id}
                            className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
                        >
                            #{tag.tagDetails?.name}
                        </span>
                    ))}
                </div>
            </div>

            <div className="mt-10">
                <ReactionBar blogId={blog.id} />
                <CommentSection blogId={blog.id} />
                <ShareButtons blogUrl={publicBlogUrl} blogTitle={blog.title} />
            </div>
        </div>
    );
};

export default AdminBlogDetail;
